import http from "@/http";
import {
  GraphicComponentSetting,
  GraphicComponentTypes,
  IGraphicsControllerStoreState,
  IGraphicUGCItem,
  IViewBox,
  PlacementSetting,
  PlacementTypes,
} from "@/libs/graphics/types/Graphics.t";
import { ActionContext, ActionTree } from "vuex";
import { MutationGraphicsController } from "./mutations";
import {
  ActionTypesGraphicsController,
  MutationTypesGraphicsController,
} from "./types";
import { AxiosResponse } from "axios";

type AugmentedActionContextGraphicsController = {
  commit<K extends keyof MutationGraphicsController>(
    key: K,
    payload: Parameters<MutationGraphicsController[K]>[1]
  ): ReturnType<MutationGraphicsController[K]>;
} & Omit<ActionContext<IGraphicsControllerStoreState, any>, "commit">;

interface ComponentGetterID {
  componentID: PlacementTypes;
  subComponentID: GraphicComponentTypes;
}

export interface ActionsGraphicsController {
  [ActionTypesGraphicsController.FETCH_GRAPHIC](
    { commit }: AugmentedActionContextGraphicsController,
    payload: {
      id: string | number;
    }
  ): Promise<string>;

  [ActionTypesGraphicsController.GET_PLACEMENT_SETTING](
    { commit, state }: AugmentedActionContextGraphicsController,
    payload: PlacementTypes
  ): PlacementSetting;

  [ActionTypesGraphicsController.GET_PLACEMENT_SETTING_SUBCOMPONENT](
    { commit, state }: AugmentedActionContextGraphicsController,
    payload: ComponentGetterID
  ): GraphicComponentSetting | undefined;

  [ActionTypesGraphicsController.ADD_GRAPHIC_UGC_ITEM](
    { commit, state, getters }: AugmentedActionContextGraphicsController,
    payload: {
      placement: PlacementTypes;
      graphicID: number;
      viewBox: IViewBox;
    }
  ): void;
}

const actions: ActionTree<IGraphicsControllerStoreState, any> &
  ActionsGraphicsController = {
  [ActionTypesGraphicsController.FETCH_GRAPHIC](
    { commit, rootState, rootGetters },
    payload
  ) {
    const url = `${rootState.endPoints.customizer.customsAssets
      }/svg/graphics/${payload.id.toString()}.svg`;
    return new Promise((resolve) => {
      http
        .get(rootGetters["utils/addCacheBusting"](url))
        .then((response: AxiosResponse<string>) => {
          resolve(response.data);
        });
    });
  },

  [ActionTypesGraphicsController.GET_PLACEMENT_SETTING](
    { commit, state },
    payload
  ) {
    return state.placementSetting.find(
      (item) => item.placementComponent === payload
    ) as PlacementSetting;
  },

  [ActionTypesGraphicsController.GET_PLACEMENT_SETTING_SUBCOMPONENT](
    { commit, state },
    payload
  ) {
    const placementSetting = state.placementSetting.find(
      (item) => item.placementComponent === payload.componentID
    );
    const subComponent =
      placementSetting &&
      placementSetting.componentSetting.find(
        (item) => item.id === payload.subComponentID
      );
    return subComponent;
  },

  [ActionTypesGraphicsController.ADD_GRAPHIC_UGC_ITEM](
    { commit },
    { placement, graphicID, viewBox }
  ) {
    const graphicUgcItem: IGraphicUGCItem = {
      id: graphicID,
      viewBox,
    };

    commit(MutationTypesGraphicsController.ADD_OR_UPDATE_GRAPHIC_UGC_ITEM, {
      placement,
      graphicUgcItem,
    });
  },
};

export default actions;
